import type * as React from 'react';
import { tss } from 'tss-react/mui';
import { ArrowLeftIcon, ArrowRightIcon } from '../icons';
import { createSmoothTransitionStyles } from './utils';
import type { CollapsedBarMode } from './collapsibleBarModeReducer.ts';

const useTriggerButtonStyles = tss.create(({ theme }) => ({
    triggerButton: {
        width: 25,
        height: 25,
        padding: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: theme.bino.color.white,
        color: 'rgb(107, 119, 140)',
        boxShadow:
            'rgba(9, 30, 66, 0.08) 0px 0px 0px 1px, rgba(9, 30, 66, 0.08) 0px 2px 4px 2px',
        boxSizing: 'border-box',
        fontSize: 12,
        lineHeight: 16,
        margin: 0,
        border: 0,
        borderRadius: '50%',
        outline: 0,
        ...createSmoothTransitionStyles(
            'background-color',
            'color',
            'transform',
        ),
        cursor: 'pointer',

        '&:hover': {
            backgroundColor: theme.bino.color.primary,
            color: theme.bino.color.white,
        },
    },
    triggerButtonIcon: {
        display: 'inherit',
    },
}));

type BaseTriggerButtonProps = {
    mode: CollapsedBarMode;
    onClick(): void;

    /**
     * This `className` handles the computed position of the `triggerButton`.
     * The position is influenced by the Jira Sidebar. If we don't want
     * this behaviour, don't use this `className`. This is passed by the `CollapsibleBar`.
     * */
    positionClassName: string;
    className?: string;
    children: React.ReactNode;
};

const TriggerButton: React.FunctionComponent<BaseTriggerButtonProps> = ({
    positionClassName,
    className,
    children,
    onClick,
}) => {
    const { classes, cx } = useTriggerButtonStyles();
    return (
        <button
            data-testid="collapsible-trigger-button"
            onClick={() => onClick()}
            className={cx(classes.triggerButton, positionClassName, className)}
        >
            <span className={classes.triggerButtonIcon}>{children}</span>
        </button>
    );
};

// Remove the `children` because the `children` element for `button`
// is created inside the `Component`.
type TriggerButtonProps = Omit<BaseTriggerButtonProps, 'children'>;

const ICON_SIZE = 9;

// Need to remove the `children` prop. This Component creates its own `children`.
export const LeftButton: React.FunctionComponent<TriggerButtonProps> = ({
    mode,
    ...props
}) => {
    return (
        <TriggerButton mode={mode} {...props}>
            {mode === 'expand' ? (
                <ArrowLeftIcon size={ICON_SIZE} />
            ) : (
                <ArrowRightIcon size={ICON_SIZE} />
            )}
        </TriggerButton>
    );
};

// Need to remove the `children` prop. This Component creates its own `children`.
export const RightButton: React.FunctionComponent<TriggerButtonProps> = ({
    mode,
    ...props
}) => {
    return (
        <TriggerButton mode={mode} {...props}>
            {mode === 'expand' ? (
                <ArrowRightIcon size={ICON_SIZE} />
            ) : (
                <ArrowLeftIcon size={ICON_SIZE} />
            )}
        </TriggerButton>
    );
};
